import moment from 'moment-timezone';

import { constants as dateAndTimeConstants } from 'utils/dateAndTimes';

import { constants as addressesConstants } from 'utils/addresses';
import setTime from 'utils/dateAndTimes/methods/setTime';
import getWorkingDay from 'utils/dateAndTimes/methods/getWorkingDay';
import isDateToday from 'utils/dateAndTimes/methods/isDateToday';
import {
  getDayDiff,
  thresholdToRound,
} from 'utils/dateAndTimes/methods/methods';

// TODO move this keys!!
const getTimeKeyFull = (shipmentKey, key) => `shipment.${shipmentKey}.${key}`;
const getTimeKey = (stopType, timeParam) => `${stopType}_${timeParam}`;

const getDateKey = (stopType, timeParam) =>
  `${stopType}_date_${timeParam.split('_')[1]}`;
const getDateFromKey = (stopType) => `${stopType}_date_from`;
const getDateToKey = (stopType) => `${stopType}_date_to`;

const getDateKeys = (stopType) => [
  getDateFromKey(stopType),
  getDateToKey(stopType),
];

const getTimeKeysByStop = (stopType) => [
  `${stopType}_${dateAndTimeConstants.TIME_FIELD_PARAMS.from}`,
  `${stopType}_${dateAndTimeConstants.TIME_FIELD_PARAMS.to}`,
];

// TODO: make test
const getMinRounded = (
  dateTime,
  stepInMinutes = dateAndTimeConstants.CONSTANTS_FOR_RULES.STEPS_IN_MINUTES
) => {
  const baseDateTime = moment(dateTime).clone();
  const minutes = baseDateTime.minute();

  if (minutes <= stepInMinutes) {
    baseDateTime.minutes(minutes === 0 ? 0 : stepInMinutes);
  }
  if (minutes > thresholdToRound(stepInMinutes)) {
    baseDateTime.add(1, 'hours').minutes(0);
  }

  return baseDateTime;
};

// TODO: make test
const getMaxRounded = (
  dateTime,
  stepInMinutes = dateAndTimeConstants.CONSTANTS_FOR_RULES.STEPS_IN_MINUTES
) => {
  const baseDateTime = moment(dateTime).clone();

  const minutes = baseDateTime.minute();

  if (minutes > thresholdToRound(stepInMinutes)) {
    baseDateTime.minutes(thresholdToRound(stepInMinutes));
  }

  return baseDateTime;
};

// TODO: make test
const getHoursRangeByStepOfMinutes = ({
  fromDateTime,
  toDateTime,
  stepInMinutes = dateAndTimeConstants.CONSTANTS_FOR_RULES.STEPS_IN_MINUTES,
  diffDaysWithCurrentDate = 0,
}) => {
  fromDateTime.set({ second: 0, millisecond: 0 });
  toDateTime.set({ second: 0, millisecond: 0 });

  const minDate = fromDateTime.clone();
  const ranges = [];

  const incrementTimeByStep = (step) => ({
    raw: fromDateTime.add(step, 'minutes').format('HH:mm'),
    diffDay: getDayDiff(minDate, fromDateTime) + diffDaysWithCurrentDate,
  });

  if (diffDaysWithCurrentDate > 0) {
    toDateTime.add(diffDaysWithCurrentDate, 'days');
    ranges.push({ isSection: true, diffDays: diffDaysWithCurrentDate });
  }

  ranges.push(incrementTimeByStep(0));
  let prevDiffDay = diffDaysWithCurrentDate;

  while (fromDateTime.isBefore(toDateTime)) {
    const { raw, diffDay } = incrementTimeByStep(stepInMinutes);
    if (diffDay !== prevDiffDay) {
      ranges.push({ isSection: true, diffDays: diffDay });
      prevDiffDay = diffDay;
    }
    ranges.push({ raw, diffDay });
  }

  return ranges;
};

const getAddressKey = (stopType) =>
  addressesConstants.ADDRESS_BY_STOP[stopType];
const getAddressRawKey = (stopType) =>
  `${addressesConstants.ADDRESS_BY_STOP[stopType]}_raw`;

/**
 * It determines if we can do a TODAY quote
 */
const isTodayAvailable = () => {
  const limitHourToday = moment().set({
    hour: dateAndTimeConstants.CONSTANTS_FOR_RULES.MAX_HOUR_TO_CREATE_TODAY,
    minute: 0,
  });
  const now = moment();
  return now.isSameOrBefore(limitHourToday);
};

const formatDateTimesToPrefill = (shipment) => ({
  pickup_date_from: shipment.pickup_date_from,
  pickup_date_to: shipment.pickup_date_to,
  dropoff_date_from: shipment.dropoff_date_from,
  dropoff_date_to: shipment.dropoff_date_to,
  pickup_time_from: moment(shipment.pickup_time_from).format('HH:mm'),
  pickup_time_to: moment(shipment.pickup_time_to).format('HH:mm'),
  dropoff_time_from: moment(shipment.dropoff_time_from).format('HH:mm'),
  dropoff_time_to: moment(shipment.dropoff_time_to).format('HH:mm'),
});

const getTodayDates = () => {
  if (!isTodayAvailable()) {
    return;
  }
  const defaultDates = moment().format('YYYY-MM-DD');

  const pickupTimeFrom = moment().add(2, 'hours').set('minute', 0).format();
  const pickupTimeTo = moment(pickupTimeFrom).add(3, 'hours').format();
  const dropoffTimeFrom = pickupTimeTo;
  const dropoffTimeTo = moment(dropoffTimeFrom).add(3, 'hours').format();
  return {
    pickup_date_from: defaultDates,
    pickup_date_to: defaultDates,
    pickup_time_from: pickupTimeFrom,
    pickup_time_to: pickupTimeTo,
    dropoff_date_from: defaultDates,
    dropoff_date_to: defaultDates,
    dropoff_time_from: dropoffTimeFrom,
    dropoff_time_to: dropoffTimeTo,
  };
};

const getDirectDates = (shipment, holidays) => {
  const date = getWorkingDay({
    date: moment(shipment?.pickup_date_from).add(
      isDateToday(shipment?.pickup_date_from || new Date()) ? 1 : 0,
      'day'
    ),
    holidays: holidays?.pickup || [],
  }).format('YYYY-MM-DD');
  return {
    pickup_date_from: date,
    pickup_date_to: date,
    pickup_time_from: setTime(date, '08:00'),
    pickup_time_to: setTime(date, '12:00'),
    dropoff_date_from: date,
    dropoff_date_to: date,
    dropoff_time_from: setTime(date, '11:00'),
    dropoff_time_to: setTime(date, '18:00'),
  };
};

export default {
  getTimeKeyFull,
  getTimeKey,
  getDateKey,
  getDateFromKey,
  getDateToKey,
  getDateKeys,
  getTimeKeysByStop,
  getMinRounded,
  getMaxRounded,
  getAddressKey,
  getAddressRawKey,
  getHoursRangeByStepOfMinutes,
  getDirectDates,
  getTodayDates,
  formatDateTimesToPrefill,
  isTodayAvailable,
};
